/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { EMPTY_FIELD_PLACEHOLDER } from '@Constants';
import { HrmRequestFormResponse } from '@Types/api/form';
import { CompanyDict, TypeDict } from '@Types/index';
import { parseDateTime } from '@Utils/transformDateTime';
import { AttendanceRequestCodes } from '../enum/AttendanceRequestTypeCodes.enum';

const prepareSource = (
  data: HrmRequestFormResponse,
  companies: CompanyDict[],
  findRequestType: (statusNum: number) => TypeDict | undefined,
  findWorkLocationType: (statusNum: number) => TypeDict | undefined,
  findDeptProjName: (projDeptID: number, wlId: number) => string | null,
  findReasonType: (statusNum: number) => TypeDict | undefined
) => {
  const {
    leaveTypeID: reqTypeId,
    reqStatusId,
    currentLocation,
    taReqToCompID,
    wlId,
    deptProjID,
    remarks,
    startDate,
    endDate,
    groupID,
  } = data;

  const requestTypeLabel = findRequestType(reqTypeId)?.codeDesc;

  const commonFields = {
    Status: reqStatusId,
    'Request Type': requestTypeLabel || '',
  };

  if (reqTypeId === AttendanceRequestCodes.ADDITIONAL_PERMANENT_ACCESS) {
    const location = findWorkLocationType(wlId)?.codeDesc;
    const depProj = findDeptProjName(deptProjID, wlId);
    const companyLabel = companies.find(
      (companyItem) => companyItem.compId === taReqToCompID
    )?.compCode;

    return {
      ...commonFields,
      'Current Location': currentLocation || EMPTY_FIELD_PLACEHOLDER,
      Company: companyLabel || EMPTY_FIELD_PLACEHOLDER,
      Location: location || EMPTY_FIELD_PLACEHOLDER,
      'Department/Project': depProj || EMPTY_FIELD_PLACEHOLDER,
      Remarks: remarks || EMPTY_FIELD_PLACEHOLDER,
    };
  }

  if (reqTypeId === AttendanceRequestCodes.TEMPORARY_ACCESS) {
    const location = findWorkLocationType(wlId)?.codeDesc;
    const depProj = findDeptProjName(deptProjID, wlId);
    const companyLabel = companies.find(
      (companyItem) => companyItem.compId === taReqToCompID
    )?.compCode;

    return {
      ...commonFields,
      'Current Location': currentLocation || EMPTY_FIELD_PLACEHOLDER,
      Company: companyLabel || EMPTY_FIELD_PLACEHOLDER,
      Location: location || EMPTY_FIELD_PLACEHOLDER,
      'Department/Project': depProj || EMPTY_FIELD_PLACEHOLDER,
      Date: parseDateTime(startDate),
      'To Date': parseDateTime(endDate),
      Remarks: remarks || EMPTY_FIELD_PLACEHOLDER,
    };
  }

  if (reqTypeId === AttendanceRequestCodes.NO_PUNCH) {
    const reasonLabel = findReasonType(groupID)?.codeDesc;

    return {
      ...commonFields,
      Date: parseDateTime(startDate),
      Reason: reasonLabel || EMPTY_FIELD_PLACEHOLDER,
      Remarks: remarks || EMPTY_FIELD_PLACEHOLDER,
    };
  }

  if (reqTypeId === AttendanceRequestCodes.LATE_PUNCH) {
    const reasonLabel = findReasonType(groupID)?.codeDesc;

    return {
      ...commonFields,
      Date: parseDateTime(startDate),
      Reason: reasonLabel || EMPTY_FIELD_PLACEHOLDER,
      Remarks: remarks || EMPTY_FIELD_PLACEHOLDER,
    };
  }

  if (reqTypeId === AttendanceRequestCodes.TECHNICAL_ISSUE) {
    const reasonLabel = findReasonType(groupID)?.codeDesc;
    const depProj = findDeptProjName(deptProjID, wlId);
    const location = findWorkLocationType(wlId)?.codeDesc;
    const companyLabel = companies.find(
      (companyItem) => companyItem.compId === taReqToCompID
    )?.compCode;

    return {
      ...commonFields,
      'Current Location': currentLocation || EMPTY_FIELD_PLACEHOLDER,
      Company: companyLabel || EMPTY_FIELD_PLACEHOLDER,
      Location: location || EMPTY_FIELD_PLACEHOLDER,
      'Department/Project': depProj || EMPTY_FIELD_PLACEHOLDER,
      Date: parseDateTime(startDate),
      Reason: reasonLabel || EMPTY_FIELD_PLACEHOLDER,
      Remarks: remarks || EMPTY_FIELD_PLACEHOLDER,
    };
  }

  // covers attendance request types added to the TARequestType dictionary
  // without a dedicated case above (e.g. External Meeting, Training)
  return {
    ...commonFields,
    Date: parseDateTime(startDate),
    Remarks: remarks || EMPTY_FIELD_PLACEHOLDER,
  };
};

interface FieldSourceProps {
  data: HrmRequestFormResponse;
  companies: CompanyDict[];
  findRequestType: (statusNum: number) => TypeDict | undefined;
  findWorkLocationType: (statusNum: number) => TypeDict | undefined;
  findDeptProjName: (projDeptID: number, wlId: number) => string | null;
  findReasonType: (statusNum: number) => TypeDict | undefined;
}

const fieldSource = ({
  data,
  companies,
  findRequestType,
  findWorkLocationType,
  findDeptProjName,
  findReasonType,
}: FieldSourceProps): Array<{
  label: string;
  value: string | number | boolean;
}> => {
  const source = prepareSource(
    data,
    companies,
    findRequestType,
    findWorkLocationType,
    findDeptProjName,
    findReasonType
  );

  return Object.entries(source).map((item) => ({
    label: item[0],
    value: item[1],
  }));
};

export default fieldSource;
